import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { invitesApi, isProduction } from '@/lib/api';
import { useAuthStore } from '@/stores/auth';
import { toast } from 'react-hot-toast';
import { CheckCircleIcon, XCircleIcon, ClockIcon } from '@heroicons/react/24/outline';

interface InviteDetails {
  invite: {
    inviteCode: string;
    email?: string;
    expiresAt: string;
  };
  organization: {
    organizationId: string;
    name: string;
    logo?: string;
    website?: string;
  };
}

export default function AcceptInvite() {
  const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuthStore();

  const [isLoading, setIsLoading] = useState(true);
  const [inviteDetails, setInviteDetails] = useState<InviteDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  useEffect(() => {
    const fetchInvite = async () => {
      if (!inviteCode) {
        setError('Invalid invite link');
        setIsLoading(false);
        return;
      }

      try {
        const response = await invitesApi.getByCode(inviteCode);
        if (response.data.success) {
          setInviteDetails(response.data.data);
        }
      } catch (err: unknown) {
        const error = err as { response?: { status?: number; data?: { error?: { message?: string } } } };
        if (error.response?.status === 404) {
          setError('This invite link is invalid or has expired.');
        } else if (error.response?.status === 410) {
          setError(error.response?.data?.error?.message || 'This invite is no longer valid.');
        } else {
          setError('Failed to load invite details.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvite();
  }, [inviteCode]);

  const handleAccept = async () => {
    if (!inviteCode) return;

    setIsAccepting(true);
    try {
      const response = await invitesApi.accept(inviteCode);
      if (response.data.success) {
        setAccepted(true);
        toast.success('You are now an affiliate!');
      }
    } catch (err: unknown) {
      const error = err as { response?: { status?: number; data?: { error?: { message?: string } } } };
      if (error.response?.status === 409) {
        toast.error('You already have an affiliate account with this organization.');
        navigate('/dashboard');
      } else {
        toast.error(error.response?.data?.error?.message || 'Failed to accept invite');
      }
    } finally {
      setIsAccepting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-rust"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-warm-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-rust/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <XCircleIcon className="w-8 h-8 text-rust" />
          </div>
          <h1 className="text-2xl font-display font-bold text-charcoal mb-2">Invalid Invite</h1>
          <p className="text-charcoal/60 mb-6">{error}</p>
          <Link to="/" className="btn btn-primary">
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-warm-white rounded-xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-sage/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircleIcon className="w-8 h-8 text-sage" />
          </div>
          <h1 className="text-2xl font-display font-bold text-charcoal mb-2">Welcome Aboard!</h1>
          <p className="text-charcoal/60 mb-6">
            You are now an affiliate for <strong>{inviteDetails?.organization.name}</strong>.
            Start creating referral links and earning commissions!
          </p>
          <Link to="/dashboard" className="btn btn-primary">
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-warm-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-charcoal text-warm-white p-6 text-center">
          {inviteDetails?.organization.logo ? (
            <img
              src={inviteDetails.organization.logo}
              alt={inviteDetails.organization.name}
              className="w-16 h-16 rounded-full mx-auto mb-3 object-cover bg-white"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-rust flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl font-bold text-white">
                {inviteDetails?.organization.name.charAt(0)}
              </span>
            </div>
          )}
          <h1 className="text-xl font-display font-bold">
            {inviteDetails?.organization.name}
          </h1>
          <p className="text-warm-white/80 text-sm mt-1">Affiliate Program</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          <div className="text-center">
            <h2 className="text-xl font-display font-bold text-charcoal mb-2">
              You&apos;ve Been Invited!
            </h2>
            <p className="text-charcoal/60">
              Join {inviteDetails?.organization.name}&apos;s affiliate program and start earning
              commissions on every sale you refer.
            </p>
          </div>

          {/* Benefits */}
          <div className="bg-cream/50 rounded-lg p-4 space-y-3">
            <h3 className="font-medium text-charcoal">What you&apos;ll get:</h3>
            <ul className="space-y-2 text-sm text-charcoal/70">
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                <span>Earn commissions on referred sales</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                <span>Access to exclusive marketing materials</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                <span>Real-time performance tracking</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircleIcon className="w-5 h-5 text-sage flex-shrink-0 mt-0.5" />
                <span>Fast, reliable payouts</span>
              </li>
            </ul>
          </div>

          {/* Expiry notice */}
          {inviteDetails?.invite.expiresAt && (
            <div className="flex items-center justify-center gap-2 text-sm text-charcoal/50">
              <ClockIcon className="w-4 h-4" />
              <span>
                Expires on {new Date(inviteDetails.invite.expiresAt).toLocaleDateString()}
              </span>
            </div>
          )}

          {/* Actions */}
          {isAuthenticated && user?.role === 'affiliate' ? (
            <button
              onClick={handleAccept}
              disabled={isAccepting}
              className="btn btn-primary w-full"
            >
              {isAccepting ? 'Accepting...' : 'Accept Invitation'}
            </button>
          ) : isAuthenticated ? (
            <div className="text-center space-y-3">
              <p className="text-sm text-charcoal/60">
                You&apos;re logged in as a {user?.role === 'org_admin' ? 'business' : 'super admin'} account.
                You need an affiliate account to accept this invite.
              </p>
              <Link
                to={isProduction() ? '/coming-soon' : `/register?invite=${inviteCode}`}
                className="btn btn-primary w-full"
              >
                Create Affiliate Account
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              <Link
                to={isProduction() ? '/coming-soon' : `/register?invite=${inviteCode}`}
                className="btn btn-primary w-full block text-center"
              >
                Sign Up & Accept
              </Link>
              <p className="text-center text-sm text-charcoal/60">
                Already have an account?{' '}
                <Link to={`/login?redirect=/invite/${inviteCode}`} className="text-rust hover:underline">
                  Log in
                </Link>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
